import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiStar, FiExternalLink } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function VideoList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () => {
    setLoading(true);
    api
      .get("/videos")
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Failed to load videos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/videos/${id}`);
      toast.success("Video deleted");
      setItems(items.filter((i) => i._id !== id));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const { data } = await api.put(`/videos/${item._id}`, { featured: !item.featured });
      setItems(items.map((i) => (i._id === item._id ? data : i)));
      toast.success(data.featured ? "Marked as featured" : "Removed from featured");
    } catch {
      toast.error("Failed to update");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl text-maroon-800">Videos & Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">YouTube product reviews, pooja ceremonies and testimonials shown on the homepage.</p>
        </div>
        <Link
          to="/admin/videos/new"
          className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <FiPlus /> Add Video
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No videos yet"
          subtitle="Click 'Add Video' and paste a YouTube link to showcase product reviews, ceremonies or testimonials on your site."
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <div key={item._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
              {/* Thumbnail */}
              <div className="relative aspect-video bg-gold-50">
                <img
                  src={`https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  ▶ YouTube
                </span>
                {item.featured && (
                  <span className="absolute top-2 right-2 bg-gold-400 text-maroon-900 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <FiStar size={11} fill="currentColor" /> Featured
                  </span>
                )}
              </div>

              {/* Body */}
              <div className="p-4 flex flex-col flex-grow">
                <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">{item.category}</span>
                <h4 className="font-heading text-maroon-800 mt-1 mb-1 line-clamp-2">{item.title}</h4>
                {item.description && <p className="text-sm text-gray-500 line-clamp-2 mb-3">{item.description}</p>}

                <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleFeatured(item)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        item.featured ? "text-gold-500" : "text-gray-300 hover:text-gold-400"
                      }`}
                      title={item.featured ? "Remove from featured" : "Mark as featured"}
                    >
                      <FiStar fill={item.featured ? "currentColor" : "none"} />
                    </button>
                    <a
                      href={`https://www.youtube.com/watch?v=${item.youtubeId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-saffron-600 hover:bg-gray-100"
                      title="Open on YouTube"
                    >
                      <FiExternalLink />
                    </a>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/videos/edit/${item._id}`} className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600">
                      <FiEdit2 />
                    </Link>
                    <button
                      onClick={() => handleDelete(item._id, item.title)}
                      className="p-2 rounded-lg hover:bg-gray-100 text-red-500"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
